import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useEffect, useRef} from 'react';

import hashed from 'hashed';

import GeoJSON from 'ol/format/GeoJSON';
import WKT from 'ol/format/WKT';

import {
  compare as compareMosaic,
  select as selectMosaic,
} from '../actions/mosaics';
import {getComparingMosaic, getSelectedMosaic} from '../selectors';
import {initialState as initialGeometryState} from '../reducers/geometry';
import {initialState as initialMapState} from '../reducers/map';
import {initialState as initialUIState} from '../reducers/ui';
import {set as setGeometry} from '../actions/geometry';
import {setLabels, setMode} from '../actions/ui';
import {setView as setMapView} from '../actions/map';

const formatGeoJSON = new GeoJSON();
const formatWKT = new WKT();

const config = {
  labels: initialUIState.labels,
  mode: initialUIState.mode,
  mosaic: '',
  comparison: '',
  center: {
    default: initialMapState.center,
    serialize: function (coord) {
      return (
        coord &&
        coord.length === 2 &&
        `${coord[0].toFixed(3)},${coord[1].toFixed(3)}`
      );
    },
    deserialize: function (str) {
      return str.split(',').map(Number);
    },
  },
  zoom: {
    default: initialMapState.zoom,
    serialize: number => number && String(Math.round(number * 100) / 100),
    deserialize: parseFloat,
  },
  geometry: {
    default: initialGeometryState.json,
    serialize: geometry =>
      geometry && formatWKT.writeGeometry(formatGeoJSON.readGeometry(geometry)),
    deserialize: geometry =>
      geometry &&
      formatGeoJSON.writeGeometryObject(formatWKT.readGeometry(geometry)),
  },
};

const Hash = ({
  compareMosaic,
  selectMosaic,
  setGeometry,
  setLabels,
  setMapView,
  setMode,
  ...props
}) => {
  const updateHashRef = useRef();

  const onHashChange = hash => {
    // labels
    if ('labels' in hash) {
      setLabels(hash.labels);
    }

    // mode
    if ('mode' in hash) {
      setMode(hash.mode);
    }

    // mosaics
    if ('mosaic' in hash) {
      selectMosaic(hash.mosaic);
    }

    // comparison
    if ('comparison' in hash) {
      compareMosaic(hash.comparison);
    }

    // map
    if ('center' in hash) {
      setMapView({center: hash.center});
    }
    if ('zoom' in hash) {
      setMapView({zoom: hash.zoom});
    }

    if ('geometry' in hash) {
      setGeometry(hash.geometry);
    }
  };

  useEffect(() => {
    if (updateHashRef.current) {
      updateHashRef.current(props);
    }
  }, [props]);

  useEffect(() => {
    updateHashRef.current = hashed.register(config, onHashChange);

    return () => {
      hashed.unregister(onHashChange);
      updateHashRef.current = null;
    };
  }, []);

  return false;
};

Hash.propTypes = {
  comparison: PropTypes.string,
  geometry: PropTypes.object,
  center: PropTypes.array,
  zoom: PropTypes.number,
  mosaic: PropTypes.string,
  mode: PropTypes.string.isRequired,
  labels: PropTypes.bool.isRequired,
  compareMosaic: PropTypes.func,
  selectMosaic: PropTypes.func,
  setGeometry: PropTypes.func,
  setLabels: PropTypes.func,
  setMapView: PropTypes.func,
  setMode: PropTypes.func,
};

const mapStateToProps = state => ({
  comparison: getComparingMosaic(state)?.name || '',
  geometry: state.geometry.json,
  center: state.map.center,
  zoom: state.map.zoom,
  mosaic: getSelectedMosaic(state)?.name || '',
  mode: state.ui.mode,
  labels: state.ui.labels,
});

const mapDispatchProps = {
  compareMosaic,
  selectMosaic,
  setGeometry,
  setLabels,
  setMapView,
  setMode,
};

export default connect(mapStateToProps, mapDispatchProps)(Hash);
